import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'

import { STORE_LOCATOR_STORE, StoreLocatorStore } from 'components/discovery/store-locator/store-locator-store.js'

import { InternalStore, StoreModel } from 'components/discovery/store-locator/typing/store-locator.js'

import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'


initializeIconsByRole({
  'csc-store-card.badge-icon': { name: 'star', style: 'solid' },
  'csc-selected-store-card.phone': { name: 'phone', style: 'light' },
  'csc-selected-store-card.mail': { name: 'envelope', style: 'light' },
})

@customElement('csc-selected-store-card')
export class CSCSelectedStoreCard extends LitElement {
  static override styles: CSSResultGroup = [hostStyle]

  @inject(STORE_LOCATOR_STORE)
  storeLocatorStore!: StoreLocatorStore

  @state()
  selectedStore: InternalStore = new StoreModel().parseToInternal()

  @property({ type: Boolean, attribute: 'show-border' })
  showBorder = false

  constructor() {
    super()

    createEffect(() => {
      const store = this.storeLocatorStore.selectedStore()
      if (store) {
        this.selectedStore = store
      }
    })
  }

  override render() {
    return html`
      <cse-paper variant="primary" ?bordered="${this.showBorder}">
        <cse-group direction="row" justify="space-between">
          <cse-group direction="column" row-gap="08">
            <cse-group direction="column" row-gap="05">
              ${this.selectedStore.premium ? html`
                <cse-badge variant="premium">
                  <cse-icon icon-role="csc-store-card.badge-icon"></cse-icon>
                  ${t('store_card_badge_text', { ns: 'csc-store-locator', default: 'Premium' })}
                </cse-badge>
              ` : nothing}

              <cse-text id="store-title" variant="heading-5" as="h3">
                ${this.selectedStore.name}
              </cse-text>

              <cse-text id="store-address" variant="body">
                <span id="store-street">${this.selectedStore.address.street} ${this.selectedStore.address.houseNumber}</span><br>
                <span id="store-zip">${this.selectedStore.address.zipcode} ${this.selectedStore.address.city}</span><br>
                <span id="store-country">${this.selectedStore.address.country}</span>
              </cse-text>

              ${this.selectedStore.phone ? html`
                <cse-link id="store-phone" role="link" url="tel:${this.selectedStore.phone}">
                  <cse-icon slot="prefix" icon-role="csc-selected-store-card.phone"></cse-icon>
                  ${this.selectedStore.phone}
                </cse-link>
              `: nothing }

              ${this.selectedStore.email ? html`
                <cse-link id="store-email" role="link" url="mailto:${this.selectedStore.email}">
                  <cse-icon slot="prefix" icon-role="csc-selected-store-card.mail"></cse-icon>
                  ${this.selectedStore.email}
                </cse-link>
              `: nothing }
            </cse-group>

            ${this.selectedStore.websiteUrl ? html`
              <cse-button
                url="${this.selectedStore.websiteUrl}"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
              >
                ${t('view_store_button_text', { ns: 'csc-store-locator', default: 'View store' })}
              </cse-button>
            `: nothing }
          </cse-group>

          ${this.selectedStore.logo ? html`
            <cse-image width="154" height="33" source="${this.selectedStore.logo}" alt="logo"></cse-image>
          ` : nothing}
        </cse-group>
      </cse-paper>
    `
  }
}
